"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "@/lib/localized-link";
import { useAuthHref } from "@/lib/use-return-to";
import { useAuth } from "@/lib/auth-context";
import { useT, useLocale } from "@/lib/locale-context";
import type { Locale } from "@/lib/i18n";
import { LogIn, User, LogOut, ChevronDown, Check, History, Sun, Moon, Monitor, Heart, Building2, Megaphone, BarChart3, Bell, Map as MapIcon, MessageSquare } from "lucide-react";
import { useTheme } from "@/lib/theme-context";
import { useSheetDrag } from "@/lib/use-sheet-drag";
import { haptic } from "@/lib/haptics";
import { FIRST_PARTY_LISTINGS } from "@/lib/flags";
import Avatar from "./Avatar";
import type { User as AuthUser } from "@/lib/auth-context";
import { THREAD_CHANGED_EVENT, loadThread } from "@/lib/feedback-thread";
import { SEGMENT_ON } from "@/lib/ui-classes";

// Language options surfaced inside the signed-in dropdown so we can
// keep the header free of a second control. Signed-out users still see
// the standalone LanguageToggle in the header.
const LOCALES: { code: Locale; label: string }[] = [
  { code: "az", label: "Az\u0259rbaycan" },
  { code: "ru", label: "\u0420\u0443\u0441\u0441\u043A\u0438\u0439" },
  { code: "en", label: "English" },
];

/**
 * Mobile-only bottom sheet variant of the signed-in menu. A top-right
 * dropdown is a desktop pattern - on a phone it hugs the thumb-hostile
 * corner and clips against the viewport edge. This sheet mirrors the
 * app's other sheets (MobileMoreSheet, SortPicker): portal to body,
 * drag-handle dismiss, RAF-gated enter so the slide-up always plays.
 * Content stays deliberately thin (identity + Profile + Sign out) -
 * everything else already lives in the More sheet.
 */
function ProfileSheet({
  open,
  onClose,
  user,
  onSignOut,
}: {
  open: boolean;
  onClose: () => void;
  user: AuthUser;
  onSignOut: () => void;
}) {
  const t = useT();
  const ANIM_MS = 200;
  const [render, setRender] = useState(false);
  const [entered, setEntered] = useState(false);
  const { offset: dragOffset, dragging, handleProps, panelRef } = useSheetDrag(onClose, open);

  useEffect(() => {
    if (open) { setRender(true); return; }
    setEntered(false);
    const timer = setTimeout(() => setRender(false), ANIM_MS);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!render || !open) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [render, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!render) return null;

  return createPortal(
    <div className="sm:hidden">
      <div
        onClick={onClose}
        aria-hidden="true"
        style={{ opacity: entered ? 1 : 0, transition: "opacity 200ms ease-out" }}
        className="fixed inset-0 z-[1110] bg-black/30 backdrop-blur-[1px]"
      />
      <div ref={panelRef}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: !entered ? "translateY(100%)" : `translateY(${dragOffset}px)`,
          transition: dragging ? "none" : "transform 200ms ease-out",
        }}
        className="fixed inset-x-0 bottom-0 z-[1120] flex flex-col overflow-hidden rounded-t-3xl bg-white pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl will-change-transform"
      >
        <div
          {...handleProps}
          role="button"
          tabIndex={0}
          aria-label={t("a11y.drag_close")}
          className="flex justify-center py-3 -mt-4 mb-1 cursor-grab active:cursor-grabbing touch-none"
        >
          <div className="h-1.5 w-10 rounded-full bg-gray-300" />
        </div>

        {/* Identity header - the one place the account is shown big. */}
        <div className="flex items-center gap-3 px-5 pb-3">
          <Avatar src={user.picture_url} name={user.name} email={user.email} size={44} />
          <div className="min-w-0">
            <div className="truncate text-sm font-bold text-gray-900">
              {user.name || t("auth.profile.title")}
            </div>
            <div className="truncate text-xs text-gray-400">{user.email}</div>
          </div>
        </div>

        <div className="border-t border-gray-100 px-3 pt-1.5">
          <Link
            href="/profile"
            onClick={onClose}
            className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-gray-900 active:bg-gray-50"
          >
            <User className="h-4.5 w-4.5 text-gray-400" />
            {t("auth.profile.title")}
          </Link>
          <button
            type="button"
            onClick={onSignOut}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-rose-600 active:bg-rose-50"
          >
            <LogOut className="h-4.5 w-4.5" />
            {t("auth.signout")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function AuthButton() {
  const { user, loading, signOut } = useAuth();
  // The corner card reads the same state on every page: the shared read
  // answers both without a second request.
  const [attention, setAttention] = useState(0);
  useEffect(() => {
    if (!user) { setAttention(0); return undefined; }
    let alive = true;
    const read = () => loadThread().then((s) => { if (alive) setAttention(s.counts?.attention ?? 0); }).catch(() => {});
    read();
    window.addEventListener(THREAD_CHANGED_EVENT, read);
    return () => { alive = false; window.removeEventListener(THREAD_CHANGED_EVENT, read); };
  }, [user]);
  const t = useT();
  const { locale, setLocale, prefetchLocales } = useLocale();
  const { theme, setTheme, resolved } = useTheme();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // The menu holds the language picker: warm the other locales' strings
  // for this page so a pick swaps the text at once.
  useEffect(() => {
    if (open) prefetchLocales();
  }, [open, prefetchLocales]);
  // Sign-in returns the visitor to the page they were on.
  const signinHref = useAuthHref("login");

  // Close dropdown when clicking outside. Desktop-only concern: on
  // mobile the menu is a portaled bottom sheet (outside menuRef), so
  // this listener would instantly dismiss it on any tap inside - the
  // sheet handles its own dismissal (backdrop / drag / Escape).
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (!window.matchMedia("(min-width: 640px)").matches) return;
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  if (loading) {
    return <div className="w-8 h-8 bg-gray-100 rounded-full animate-pulse" />;
  }

  if (user) {
    return (
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => {
            setOpen((v) => {
              if (!v) haptic(8);
              return !v;
            });
          }}
          className="flex items-center gap-1.5 cursor-pointer rounded-full hover:ring-2 hover:ring-gray-200 transition-all"
        >
          <span className="relative inline-flex">
            <Avatar src={user.picture_url} name={user.name} email={user.email} size={32} />
            {/* A conversation wants the person: a dot on the avatar itself,
                never a number (owner, 2026-10-26). */}
            {attention > 0 && (
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white" aria-hidden />
            )}
          </span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* Mobile gets a bottom sheet instead of this dropdown. */}
        <ProfileSheet
          open={open}
          onClose={() => setOpen(false)}
          user={user}
          onSignOut={() => {
            setOpen(false);
            signOut();
          }}
        />

        {open && (
          <div className="hidden sm:block absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-1 z-50">
            {/* User info header */}
            <div className="px-4 py-3 border-b border-gray-100">
              <div className="text-sm font-semibold text-gray-900 truncate">
                {user.name || t("auth.profile.title")}
              </div>
              <div className="text-xs text-gray-400 truncate">{user.email}</div>
            </div>

            {/* Menu items */}
            <Link
              href="/profile"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <User className="w-4 h-4 text-gray-400" />
              {t("auth.profile.title")}
            </Link>
            {/* Mobile splits the dropdown's secondary entries (history,
                language, theme) into the bottom MobileMoreSheet so the
                top dropdown stays a thin Profile + Sign-out menu and
                the user has one clear place to find each control. The
                hidden-sm-up reveals these entries on tablet+ where the
                More sheet doesn't render. */}
            <Link
              href="/history"
              onClick={() => setOpen(false)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <History className="w-4 h-4 text-gray-400" />
              {t("nav.history")}
            </Link>
            <Link
              href="/watchlist"
              onClick={() => setOpen(false)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <Heart className="w-4 h-4 text-gray-400" />
              {t("watchlist.title")}
            </Link>
            {/* Every personal page lives here and only here (owner,
                2026-10-11): the alerts and the person's own map join,
                the AI report goes back to the Qiymətləndir menu. */}
            <Link
              href="/alerts"
              onClick={() => setOpen(false)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <Bell className="w-4 h-4 text-gray-400" />
              {t("nav.alerts")}
            </Link>
            <Link
              href="/properties"
              onClick={() => setOpen(false)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <Building2 className="w-4 h-4 text-gray-400" />
              {t("myproperties.title")}
            </Link>
            <Link
              href="/listing-analytics"
              onClick={() => setOpen(false)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <BarChart3 className="w-4 h-4 text-gray-400" />
              {t("la.title")}
            </Link>
            <Link
              href="/map/personal"
              onClick={() => setOpen(false)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <MapIcon className="w-4 h-4 text-gray-400" />
              {t("map.layer.personal")}
            </Link>
            {FIRST_PARTY_LISTINGS && (
              <Link
                href="/profile/listings"
                onClick={() => setOpen(false)}
                className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <Megaphone className="w-4 h-4 text-gray-400" />
                {t("mylistings.title")}
              </Link>
            )}
            {/* A way to reach us from every page (owner, 2026-10-13: contact
                was buried in the Company group and the footer). */}
            <Link
              href={attention > 0 ? "/contact#conversations" : "/contact"}
              onClick={() => setOpen(false)}
              className="hidden sm:flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <MessageSquare className="w-4 h-4 text-gray-400" />
              {t("nav.write_us")}
              {/* Conversations that want the person: a question unanswered,
                  a reply unread (owner, 2026-10-26: "show it on the profile
                  on the top right with the number"). */}
              {attention > 0 && (
                <span className="ml-auto h-2 w-2 rounded-full bg-rose-500" aria-label={t("contact.conv.unread")} />
              )}
            </Link>

            {/* Language picker - lives inside this dropdown when the
                user is signed in so the header stays uncluttered. The
                standalone LanguageToggle renders only for logged-out
                visitors in SharedHeader. Hidden on mobile - same
                control surfaces in the bottom MobileMoreSheet. */}
            <div className="hidden sm:block border-t border-gray-100 mt-1 pt-1">
              <div className="px-4 pt-1 pb-0.5 text-[10px] text-gray-400 font-semibold">
                {t("auth.menu.language")}
              </div>
              {LOCALES.map((l) => {
                const active = locale === l.code;
                return (
                  <button
                    key={l.code}
                    onClick={() => {
                      setLocale(l.code);
                      // Leave the menu open so the user can see the check
                      // move to the row they just picked before they
                      // dismiss manually.
                    }}
                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm transition-colors cursor-pointer ${
                      active ? "bg-gray-100 font-semibold text-gray-900" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="flex-1 text-left">{l.label}</span>
                    {active && <Check className="w-4 h-4 text-gray-900" />}
                  </button>
                );
              })}
            </div>

            {/* Theme toggle - compact Sun/Moon switch. Hidden on
                mobile; same control surfaces in the bottom More sheet. */}
            <div className="hidden sm:flex border-t border-gray-100 mt-1 px-4 py-2.5 items-center justify-between">
              <span className="text-[10px] text-gray-400 font-semibold">
                {t("auth.menu.theme")}
              </span>
              <div className="flex items-center bg-gray-100 rounded-full p-0.5">
                <button
                  type="button"
                  onClick={() => setTheme("light")}
                  className={`p-1.5 rounded-full transition-all ${
                    resolved === "light"
                      ? SEGMENT_ON
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                  aria-label={t("theme.light")}
                >
                  <Sun className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setTheme("system")}
                  className={`p-1.5 rounded-full transition-all ${
                    theme === "system"
                      ? SEGMENT_ON
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                  aria-label={t("theme.system")}
                >
                  <Monitor className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setTheme("dark")}
                  className={`p-1.5 rounded-full transition-all ${
                    resolved === "dark" && theme !== "system"
                      ? SEGMENT_ON
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                  aria-label={t("theme.dark")}
                >
                  <Moon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                setOpen(false);
                signOut();
              }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer border-t border-gray-100"
            >
              <LogOut className="w-4 h-4 text-gray-400" />
              {t("auth.signout")}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      href={signinHref}
      className="flex h-8 items-center gap-1.5 px-2.5 text-xs font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-600 cursor-pointer whitespace-nowrap"
    >
      <LogIn className="w-3.5 h-3.5" />
      {t("auth.signin")}
    </Link>
  );
}
